<template>
  <div class="w-full text-left">
    <p v-if="label" class="text-textPrimary mb-2">{{ label }}</p>
    <div class="inline-flex flex-wrap items-center gap-2 p-1.5 rounded-lg bg-gray-200">
      <button
        v-for="option in options"
        :key="option.value"
        :data-testid="`option-select-${option.value}`"
        @click="toggleSelection(option.value)"
        class="px-4 py-1.5 rounded-md text-sm font-medium transition-colors duration-200 focus:outline-none"
        :class="
          isSelected(option.value)
            ? 'bg-white text-gray-600 shadow-sm'
            : 'text-gray-600 hover:bg-gray-200'
        "
      >
        {{ option.label }}
      </button>
    </div>
    <p v-if="errorMessages" class="text-red-500 text-xs mt-1">{{ errorMessages }}</p>
  </div>
</template>

<script lang="ts" setup>
import { ref, PropType, watch } from 'vue'

interface SelectorOption {
  value: string | number
  label: string
}

const props = defineProps({
  label: {
    type: String,
    default: ''
  },
  options: {
    type: Array as PropType<SelectorOption[]>,
    required: true
  },
  modelValue: {
    type: [String, Number, Array] as PropType<string | number | (string | number)[]>,
    required: true
  },
  multiple: {
    type: Boolean,
    default: false
  },
  errorMessages: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue'])

const localSelection = ref(props.modelValue)

watch(
  () => props.modelValue,
  (newValue) => {
    localSelection.value = newValue
  }
)

const isSelected = (value: string | number) => {
  if (props.multiple && Array.isArray(localSelection.value)) {
    return localSelection.value.includes(value)
  }
  return localSelection.value === value
}

const toggleSelection = (value: string | number) => {
  if (props.multiple) {
    const selection = new Set(Array.isArray(localSelection.value) ? localSelection.value : [])
    if (selection.has(value)) {
      selection.delete(value)
    } else {
      selection.add(value)
    }
    localSelection.value = Array.from(selection)
  } else {
    localSelection.value = value
  }
  emit('update:modelValue', localSelection.value)
}
</script>
